import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert, Empty, Input, Segmented, Select, Spin, Switch, Tag, message } from 'antd';
import { CalendarOutlined, ExportOutlined, SearchOutlined } from '@ant-design/icons';
import type {
  FieldDefinition,
  MyItem,
  MyItemsResponse,
  MyItemsRoleFilter,
  Space,
} from '@lens/shared';
import { ApiError } from '../../shared/api/client.ts';
import { Chip } from '../../shared/components/Chip.tsx';
import { SpaceAvatar } from '../../shared/components/SpaceAvatar.tsx';
import { readPref, writePref } from '../../shared/prefs/store.ts';
import { getItem, patchItem } from '../items/api.ts';
import type { ItemRowWithRefs } from '../items/api.ts';
import { ItemDrawer } from '../items/ItemDrawer.tsx';
import { getItemType } from '../item-types/api.ts';
import { getSpace } from '../spaces/api.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { fetchMyItems } from './api.ts';
import styles from './MyPage.module.css';
import { useDocumentTitle } from '../../shared/hooks/useDocumentTitle.ts';

/**
 * Страница «Моё» (`/my`) — все записи текущего пользователя сквозь
 * пространства: где он исполнитель (user-поля) и/или автор.
 *
 * Группировка по категории статуса (В работе → Бэклог → Без статуса →
 * Готово → Отменено), внутри группы — по дедлайну (без срока — в конец),
 * затем по свежести. Клик по строке открывает карточку записи (ItemDrawer)
 * прямо на странице — `?item=&space=` в URL, «назад» браузера закрывает
 * карточку, уходить в пространство не нужно; переход в пространство —
 * отдельной иконкой в строке.
 *
 * Фильтры (роль — серверная; поиск/пространство/тип/просроченные —
 * клиентские, список ≤200 уже на клиенте) персистятся per-user через
 * prefs-store (`ui:myFilters`, тот же путь, что «Избранное»): гидратируются
 * при логине до монтирования, так что уход со страницы и возврат их не
 * теряют.
 */

type GroupKey = 'active' | 'backlog' | 'none' | 'done' | 'cancelled';

const GROUP_ORDER: GroupKey[] = ['active', 'backlog', 'none', 'done', 'cancelled'];
const GROUP_LABELS: Record<GroupKey, string> = {
  active: 'В работе',
  backlog: 'Бэклог',
  none: 'Без статуса',
  done: 'Готово',
  cancelled: 'Отменено',
};
/** Цвет акцента группы: статусные CSS-токены, у «Без статуса» — серый. */
const GROUP_ACCENT: Record<GroupKey, string> = {
  active: 'var(--status-active-dot, #1677ff)',
  backlog: 'var(--status-backlog-dot, #8c8c8c)',
  none: 'var(--color-text-secondary, #9a97a6)',
  done: 'var(--status-done-dot, #52c41a)',
  cancelled: 'var(--status-cancelled-dot, #bfbfbf)',
};

const ROLE_OPTIONS = [
  { label: 'Все', value: 'all' },
  { label: 'Я исполнитель', value: 'assignee' },
  { label: 'Я автор', value: 'author' },
] as const;

const EMPTY_TEXT: Record<MyItemsRoleFilter, string> = {
  all: 'Нет записей, где вы исполнитель или автор',
  assignee: 'Нет записей, где вы исполнитель',
  author: 'Нет записей, где вы автор',
};

// ─── Персистентные фильтры ────────────────────────────────────────────────
const FILTERS_KEY = 'ui:myFilters';
const ROLE_VALUES: MyItemsRoleFilter[] = ['all', 'assignee', 'author'];

interface PersistedFilters {
  role: MyItemsRoleFilter;
  q: string;
  spaces: string[];
  types: string[];
  overdue: boolean;
}

const DEFAULT_FILTERS: PersistedFilters = {
  role: 'all',
  q: '',
  spaces: [],
  types: [],
  overdue: false,
};

/** Читает сохранённые фильтры из prefs-кэша, отбрасывая мусор. */
function loadFilters(): PersistedFilters {
  const raw = readPref<Partial<PersistedFilters>>(FILTERS_KEY);
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_FILTERS };
  const strArray = (v: unknown): string[] =>
    Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  return {
    role: ROLE_VALUES.includes(raw.role as MyItemsRoleFilter) ? (raw.role as MyItemsRoleFilter) : 'all',
    q: typeof raw.q === 'string' ? raw.q : '',
    spaces: strArray(raw.spaces),
    types: strArray(raw.types),
    overdue: raw.overdue === true,
  };
}

interface ListState {
  data: MyItemsResponse | null;
  loading: boolean;
  error: string | null;
}

interface DrawerData {
  item: ItemRowWithRefs;
  fields: FieldDefinition[];
  space: Space;
}

function groupKeyOf(item: MyItem): GroupKey {
  return item.status?.category ?? 'none';
}

/** Дедлайн раньше — выше; без дедлайна — в конец; затем свежие изменения. */
function compareWithinGroup(a: MyItem, b: MyItem): number {
  if (a.dueDate !== null || b.dueDate !== null) {
    if (a.dueDate === null) return 1;
    if (b.dueDate === null) return -1;
    if (a.dueDate !== b.dueDate) return a.dueDate < b.dueDate ? -1 : 1;
  }
  return a.updatedAt < b.updatedAt ? 1 : a.updatedAt > b.updatedAt ? -1 : 0;
}

function formatDue(iso: string): string {
  const date = new Date(`${iso}T00:00:00`);
  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
}

function todayIso(): string {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
}

/** Просрочено: срок в прошлом, а запись не в done/cancelled. */
function isOverdue(item: MyItem): boolean {
  if (item.dueDate === null) return false;
  const category = item.status?.category;
  if (category === 'done' || category === 'cancelled') return false;
  return item.dueDate < todayIso();
}

/** Ссылка «открыть в пространстве» (полный контекст: грид, представления). */
function spaceUrl(item: MyItem): string {
  return `/s/${item.spaceSlug}?item=${encodeURIComponent(item.displayKey ?? item.itemId)}`;
}

interface RowProps {
  item: MyItem;
  onOpen: (item: MyItem) => void;
}

function MyItemRow({ item, onOpen }: RowProps): JSX.Element {
  const overdue = isOverdue(item);
  const authorOnly = item.roles.includes('author') && !item.roles.includes('assignee');
  return (
    <div
      role="button"
      tabIndex={0}
      className={styles.row}
      data-testid="my-item-row"
      onClick={() => onOpen(item)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(item);
        }
      }}
    >
      <span className={styles.displayKey}>{item.displayKey ?? '—'}</span>
      <span className={styles.titleCell}>
        <span className={styles.rowTitle}>{item.title ?? 'Без названия'}</span>
        {authorOnly && <Tag className={styles.roleTag}>автор</Tag>}
      </span>
      <span className={styles.statusCell}>
        {item.status &&
          (item.status.category ? (
            <Chip label={item.status.label} tone="status" category={item.status.category} />
          ) : (
            <Chip label={item.status.label} tone="select" />
          ))}
      </span>
      <span className={styles.dueCell}>
        {item.dueDate && (
          <span
            className={`${styles.dueBadge} ${overdue ? styles.dueOverdue : ''}`}
            title={
              (item.dueFieldName ? `${item.dueFieldName}: ` : '') +
              (overdue ? 'просрочено' : '')
            }
          >
            <CalendarOutlined className={styles.dueIcon} />
            {formatDue(item.dueDate)}
          </span>
        )}
      </span>
      <span className={styles.spaceCell} title={`${item.spaceName} · ${item.itemTypeName}`}>
        <SpaceAvatar name={item.spaceName} icon={item.spaceIcon} seed={item.spaceId} size={22} />
        <span className={styles.spaceText}>
          <span className={styles.spaceName}>{item.spaceName}</span>
          <span className={styles.typeName}>{item.itemTypeName}</span>
        </span>
      </span>
      <Link
        to={spaceUrl(item)}
        className={styles.spaceLink}
        title="Открыть в пространстве"
        aria-label="Открыть в пространстве"
        data-testid="my-item-space-link"
        onClick={(e) => e.stopPropagation()}
      >
        <ExportOutlined />
      </Link>
    </div>
  );
}

export function MyPage(): JSX.Element {
  useDocumentTitle('Моё');
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Начальные значения — из persisted-фильтров (prefs гидратированы при логине).
  const [initial] = useState(loadFilters);
  const [role, setRole] = useState<MyItemsRoleFilter>(initial.role);
  const [q, setQ] = useState(initial.q);
  const [spaceFilter, setSpaceFilter] = useState<string[]>(initial.spaces);
  const [typeFilter, setTypeFilter] = useState<string[]>(initial.types);
  const [overdueOnly, setOverdueOnly] = useState(initial.overdue);

  const [state, setState] = useState<ListState>({ data: null, loading: true, error: null });
  const [reloadToken, setReloadToken] = useState(0);

  // Персистенция фильтров (кэш + debounced-PUT на сервер).
  useEffect(() => {
    writePref(FILTERS_KEY, {
      role,
      q,
      spaces: spaceFilter,
      types: typeFilter,
      overdue: overdueOnly,
    } satisfies PersistedFilters);
  }, [role, q, spaceFilter, typeFilter, overdueOnly]);

  // ── Список (перезапрашивается только при смене роли — она серверная) ──
  useEffect(() => {
    const controller = new AbortController();
    setState((current) => ({ ...current, loading: true, error: null }));
    fetchMyItems(role, controller.signal)
      .then((data) => setState({ data, loading: false, error: null }))
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        const msg =
          error instanceof ApiError
            ? error.message || 'Не удалось загрузить записи'
            : 'Не удалось загрузить записи';
        setState((current) => ({ ...current, loading: false, error: msg }));
      });
    return () => controller.abort();
  }, [role, reloadToken]);

  const reloadSilent = useCallback(() => setReloadToken((t) => t + 1), []);

  // ── Карточка записи (ItemDrawer по ?item=&space=) ──
  const itemParam = searchParams.get('item');
  const spaceParam = searchParams.get('space');
  const [drawer, setDrawer] = useState<{ loading: boolean; data: DrawerData | null }>({
    loading: false,
    data: null,
  });
  // Последняя открытая запись — чтобы «поздний» blur после закрытия карточки
  // всё-таки сохранился (см. handleFieldSave).
  const lastDrawerDataRef = useRef<DrawerData | null>(null);
  if (drawer.data) lastDrawerDataRef.current = drawer.data;

  useEffect(() => {
    if (!itemParam || !spaceParam) {
      setDrawer({ loading: false, data: null });
      return;
    }
    let cancelled = false;
    setDrawer((current) => ({ ...current, loading: true }));
    void (async () => {
      const item = (await getItem(spaceParam, itemParam)) as ItemRowWithRefs;
      const [space, itemType] = await Promise.all([
        getSpace(spaceParam),
        getItemType(spaceParam, item.itemTypeId),
      ]);
      if (cancelled) return;
      setDrawer({ loading: false, data: { item, fields: itemType.fields, space } });
    })().catch((error: unknown) => {
      if (cancelled) return;
      setDrawer({ loading: false, data: null });
      void message.error(
        error instanceof ApiError ? error.message : 'Не удалось открыть запись',
      );
    });
    return () => {
      cancelled = true;
    };
  }, [itemParam, spaceParam]);

  const openItem = useCallback(
    (item: MyItem) => {
      setSearchParams({ item: item.itemId, space: item.spaceId });
    },
    [setSearchParams],
  );

  const closeDrawer = useCallback(() => {
    setSearchParams({});
  }, [setSearchParams]);

  const handleFieldSave = useCallback(
    async (fieldSlug: string, value: unknown) => {
      // drawer.data ?? lastDrawerDataRef: blur поля может прийти уже после
      // закрытия карточки — тогда data пустая, и раньше сохранение молча
      // терялось вместе с введённым текстом.
      const data = drawer.data ?? lastDrawerDataRef.current;
      if (!data) return;
      try {
        const updated = await patchItem(data.space.id, data.item.id, {
          fields: { [fieldSlug]: value },
        });
        setDrawer((current) =>
          current.data
            ? {
                ...current,
                data: {
                  ...current.data,
                  item: {
                    ...updated,
                    resolvedRefs: { ...current.data.item.resolvedRefs, ...updated.resolvedRefs },
                  },
                },
              }
            : current,
        );
        // Статус/дедлайн могли измениться — обновляем список (без очистки UI).
        reloadSilent();
      } catch (error) {
        void message.error(error instanceof ApiError ? error.message : 'Не удалось сохранить');
      }
    },
    [drawer.data, reloadSilent],
  );

  // ── Фильтрация и группировка ──
  const allItems = useMemo(() => state.data?.items ?? [], [state.data]);

  const spaceOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const item of allItems) if (!seen.has(item.spaceId)) seen.set(item.spaceId, item.spaceName);
    return [...seen.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'ru'));
  }, [allItems]);

  const typeOptions = useMemo(() => {
    const seen = new Set<string>();
    for (const item of allItems) seen.add(item.itemTypeName);
    return [...seen].sort((a, b) => a.localeCompare(b, 'ru')).map((name) => ({ value: name, label: name }));
  }, [allItems]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return allItems.filter((item) => {
      if (spaceFilter.length > 0 && !spaceFilter.includes(item.spaceId)) return false;
      if (typeFilter.length > 0 && !typeFilter.includes(item.itemTypeName)) return false;
      if (overdueOnly && !isOverdue(item)) return false;
      if (needle !== '') {
        const haystack = `${item.title ?? ''} ${item.displayKey ?? ''}`.toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      return true;
    });
  }, [allItems, q, spaceFilter, typeFilter, overdueOnly]);

  const groups = useMemo(() => {
    const byKey = new Map<GroupKey, MyItem[]>();
    for (const item of filtered) {
      const key = groupKeyOf(item);
      const list = byKey.get(key);
      if (list) list.push(item);
      else byKey.set(key, [item]);
    }
    return GROUP_ORDER.filter((key) => byKey.has(key)).map((key) => ({
      key,
      label: GROUP_LABELS[key],
      items: byKey.get(key)!.sort(compareWithinGroup),
    }));
  }, [filtered]);

  const total = state.data?.total ?? 0;
  const hasFilters =
    q.trim() !== '' || spaceFilter.length > 0 || typeFilter.length > 0 || overdueOnly;
  const initialLoading = state.loading && state.data === null;
  const showTable = !initialLoading && !state.error && filtered.length > 0;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>
          Моё
          {state.data && (
            <span className={styles.counter}>
              {hasFilters ? `${filtered.length} из ${total}` : total}
            </span>
          )}
        </h1>
        <Segmented
          options={[...ROLE_OPTIONS]}
          value={role}
          onChange={(value) => setRole(value as MyItemsRoleFilter)}
        />
      </div>

      <div className={styles.toolbar}>
        <Input
          allowClear
          prefix={<SearchOutlined className={styles.searchIcon} />}
          placeholder="Название или ключ…"
          className={styles.search}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <Select
          mode="multiple"
          allowClear
          placeholder="Пространство"
          className={styles.filterSelect}
          maxTagCount="responsive"
          options={spaceOptions}
          value={spaceFilter}
          onChange={setSpaceFilter}
        />
        <Select
          mode="multiple"
          allowClear
          placeholder="Тип записи"
          className={styles.filterSelect}
          maxTagCount="responsive"
          options={typeOptions}
          value={typeFilter}
          onChange={setTypeFilter}
        />
        <label className={styles.overdueToggle}>
          <Switch size="small" checked={overdueOnly} onChange={setOverdueOnly} />
          Просроченные
        </label>
      </div>

      {initialLoading && (
        <div className={styles.centered}>
          <Spin size="large" />
        </div>
      )}

      {!initialLoading && state.error && (
        <Alert
          type="error"
          message={state.error}
          showIcon
          action={
            <a onClick={reloadSilent} role="button">
              Повторить
            </a>
          }
        />
      )}

      {!initialLoading && !state.error && filtered.length === 0 && (
        <Empty
          className={styles.empty}
          description={hasFilters ? 'Ничего не найдено по фильтрам' : EMPTY_TEXT[role]}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}

      {showTable && (
        <div className={styles.table}>
          <div className={`${styles.row} ${styles.headRow}`}>
            <span>Ключ</span>
            <span>Название</span>
            <span>Статус</span>
            <span>Срок</span>
            <span>Пространство</span>
            <span />
          </div>
          {groups.map((group) => (
            <div key={group.key} className={styles.group}>
              <div
                className={styles.groupBand}
                data-testid="my-group"
                style={{ ['--group-accent' as string]: GROUP_ACCENT[group.key] }}
              >
                <span className={styles.groupDot} />
                {group.label}
                <span className={styles.groupCount}>{group.items.length}</span>
              </div>
              {group.items.map((item) => (
                <MyItemRow key={item.itemId} item={item} onOpen={openItem} />
              ))}
            </div>
          ))}
        </div>
      )}

      <ItemDrawer
        open={!!itemParam && !!spaceParam && (drawer.loading || !!drawer.data)}
        onClose={closeDrawer}
        item={drawer.data?.item ?? null}
        fields={drawer.data?.fields ?? []}
        spaceId={drawer.data?.space.id ?? spaceParam ?? ''}
        spaceSlug={drawer.data?.space.slug}
        itemTypeId={drawer.data?.item.itemTypeId}
        currentUserId={user?.id ?? ''}
        myRole={drawer.data?.space.myRole}
        jiraEnabled={drawer.data?.space.jiraIntegrationEnabled ?? false}
        hermesEnabled={drawer.data?.space.hermesIntegrationEnabled ?? false}
        resolvedRefs={drawer.data?.item.resolvedRefs}
        onFieldSave={(fieldSlug, value) => void handleFieldSave(fieldSlug, value)}
      />
    </div>
  );
}
